import { redirect } from "@tanstack/react-router";
import { trpcStandalone } from "./trpcStandalone";

/**
 * Client-side auth gate used by route `beforeLoad`s, replacing the Next
 * middleware session-cookie check. The session cookie is httpOnly, so the client
 * cannot read it directly — instead we ask the server (auth.me, which 401s when
 * unauthenticated). The result is memoized so sub-navigations don't re-hit the
 * network; `clearAuthCache()` resets it on sign-out.
 */
let authKnownGood = false;

/** Synchronous read of the memoized auth state (no network). */
export function isAuthedCached(): boolean {
  return authKnownGood;
}

export async function isAuthed(): Promise<boolean> {
  if (authKnownGood) return true;
  try {
    await trpcStandalone.auth.me.query();
    authKnownGood = true;
    return true;
  } catch {
    return false;
  }
}

export function markAuthed() {
  authKnownGood = true;
}

export function clearAuthCache() {
  authKnownGood = false;
}

/**
 * Guard for authed route `beforeLoad`s. Returns `void` (synchronously) once auth
 * is known-good so navigation stays synchronous — an async beforeLoad makes
 * TanStack defer the render (pending transition), leaving the previous route on
 * screen while the URL has already changed. Only the first, uncached call awaits
 * the server; on failure it throws a redirect to /sign-in.
 */
export function requireAuth(currentHref: string): void | Promise<void> {
  if (authKnownGood) return;
  return isAuthed().then((ok) => {
    if (!ok) {
      throw redirect({ to: "/sign-in", search: { redirect: currentHref } });
    }
  });
}
